import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/feature/SectionHeading";
import ArticleCard from "@/components/feature/ArticleCard";
import FaqAccordion from "@/components/feature/FaqAccordion";
import NewsTicker from "@/components/feature/NewsTicker";
import HomeHero from "@/pages/home/components/HomeHero";
import HotBoard from "@/pages/home/components/HotBoard";
import { articleList } from "@/mocks/articles";
import { categoryList, faqList, siteInfo, testimonialList } from "@/mocks/site";
import type { Article } from "@/types/content";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      name: siteInfo.name,
      url: "https://fvwyz.cn/",
      description: siteInfo.description,
      email: siteInfo.email,
    },
    {
      "@type": "WebSite",
      name: siteInfo.name,
      url: "https://fvwyz.cn/",
      inLanguage: "zh-CN",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://fvwyz.cn/articles?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "FAQPage",
      mainEntity: faqList.slice(0, 4).map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ],
};

export default function Home() {
  const articles: Article[] = articleList;
  const latest = articles.slice(0, 6);
  const featured = articles.filter((item) => item.featured);

  return (
    <SiteLayout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(homeJsonLd) }} />

      <HomeHero />
      <NewsTicker />

      <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <HotBoard articles={articles} />
      </div>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="持续滚动更新"
            title="最新吃瓜动态"
            description="编辑团队按热度整理当天正在发酵的话题，出现新动向会第一时间补充进对应条目。"
            to="/articles"
            linkLabel="查看全部动态"
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {latest.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="按兴趣筛选"
          title="热门分类"
          description="从娱乐八卦到时尚红毯，选择一个方向，只看你真正关心的话题。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {categoryList.map((cat) => (
            <Link
              key={cat.slug}
              to={`/articles?category=${cat.slug}`}
              className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 hover:bg-primary-50"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800 transition-colors group-hover:bg-accent-500 group-hover:text-secondary-950">
                <i className={`${cat.icon} w-6 h-6 flex items-center justify-center`}></i>
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-base font-bold text-foreground-950">{cat.name}</h3>
                  <span className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-600">
                    {cat.count} 篇
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{cat.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="深度复盘"
            title="今日热议专题"
            description="不只是碎片消息，我们把讨论度最高的话题整理成完整脉络，方便你一次看清来龙去脉。"
          />
          <div className="flex flex-col gap-5">
            {featured.map((article) => (
              <ArticleCard key={article.id} article={article} variant="wide" />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="他们怎么说"
          title="用户口碑"
          description="来自真实浏览习惯的反馈，也让我们知道下一步该把哪里做得更好。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {testimonialList.map((item) => (
            <figure key={item.name} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className="ri-double-quotes-l w-5 h-5 flex items-center justify-center"></i>
              </span>
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.content}</blockquote>
              <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                  {item.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground-950">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto grid w-full grid-cols-1 gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <span className="mb-2 inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
              常见问题
            </span>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              关于热搜吃瓜，你可能想知道的
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              更新频率、内容来源、榜单计算方式，我们把最常被问到的几个问题整理在了一起。
            </p>
            <Link
              to="/faq"
              className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-foreground-800"
            >
              查看全部问题
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </span>
            </Link>
          </div>
          <div className="lg:col-span-2">
            <FaqAccordion items={faqList.slice(0, 4)} />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}